import React, { useState } from 'react';

const Projects = () => {
  const [activeTab, setActiveTab] = useState('academic');

  const projectCategories = {
    academic: {
      title: 'Academic Projects',
      subtitle: 'Final Year & Semester Projects to Excel in Your Studies',
      icon: '🎓',
      projects: [
        {
          title: 'E-Commerce Web Application',
          description: 'Full-stack e-commerce platform with React frontend, Node.js backend, and MongoDB database. Includes payment integration, user authentication, and admin panel.',
          technologies: ['React', 'Node.js', 'MongoDB', 'Stripe API'],
          duration: '4-6 months',
          difficulty: 'Advanced',
          category: 'Web Development',
          features: ['User Authentication', 'Payment Gateway', 'Inventory Management', 'Order Tracking', 'Admin Dashboard']
        },
        {
          title: 'Machine Learning Stock Predictor',
          description: 'AI-powered stock price prediction system using historical data analysis and machine learning algorithms. Includes data visualization and real-time predictions.',
          technologies: ['Python', 'TensorFlow', 'Pandas', 'Flask', 'Chart.js'],
          duration: '3-4 months',
          difficulty: 'Advanced',
          category: 'Data Science',
          features: ['Data Analysis', 'ML Algorithms', 'Real-time Predictions', 'Interactive Charts', 'API Integration']
        },
        {
          title: 'Smart Campus Management System',
          description: 'Comprehensive campus management solution with student portal, faculty dashboard, and administrative tools. Includes attendance tracking and grade management.',
          technologies: ['React', 'Java Spring Boot', 'MySQL', 'JWT'],
          duration: '5-6 months',
          difficulty: 'Advanced',
          category: 'Enterprise Software',
          features: ['Student Portal', 'Faculty Dashboard', 'Attendance System', 'Grade Management', 'Event Calendar']
        },
        {
          title: 'IoT-Based Home Automation',
          description: 'Smart home system with mobile app control, sensor integration, and real-time monitoring. Perfect for ECE and CSE final year projects.',
          technologies: ['Arduino', 'React Native', 'Firebase', 'Sensors'],
          duration: '3-4 months',
          difficulty: 'Intermediate',
          category: 'IoT',
          features: ['Mobile Control', 'Sensor Integration', 'Real-time Monitoring', 'Voice Commands', 'Energy Analytics']
        }
      ]
    },
    internal: {
      title: 'Internal Company Projects',
      subtitle: 'Real Enterprise Projects from Our Industry Partners',
      icon: '🏢',
      projects: [
        {
          title: 'Banking Portal Modernization',
          description: 'Modernizing legacy banking systems with microservices architecture, improving performance and user experience for a leading bank.',
          technologies: ['Angular', 'Spring Boot', 'PostgreSQL', 'Docker'],
          duration: '6-8 months',
          difficulty: 'Expert',
          category: 'Financial Services',
          features: ['Microservices', 'Security Implementation', 'Performance Optimization', 'User Experience', 'API Gateway']
        },
        {
          title: 'Healthcare Management Platform',
          description: 'Comprehensive healthcare platform for patient management, appointment scheduling, and medical records with HIPAA compliance.',
          technologies: ['Vue.js', 'Python Django', 'PostgreSQL', 'AWS'],
          duration: '8-10 months',
          difficulty: 'Expert',
          category: 'Healthcare',
          features: ['Patient Management', 'Appointment System', 'Medical Records', 'HIPAA Compliance', 'Telemedicine']
        },
        {
          title: 'Supply Chain Analytics Dashboard',
          description: 'Real-time analytics dashboard for supply chain optimization, inventory management, and predictive analytics for manufacturing companies.',
          technologies: ['React', 'D3.js', 'Python', 'Apache Kafka'],
          duration: '4-6 months',
          difficulty: 'Advanced',
          category: 'Analytics',
          features: ['Real-time Analytics', 'Predictive Models', 'Interactive Dashboards', 'Data Visualization', 'Alert Systems']
        },
        {
          title: 'Digital Marketing Automation Tool',
          description: 'Marketing automation platform with campaign management, customer segmentation, and ROI analytics for digital marketing agencies.',
          technologies: ['React', 'Node.js', 'MongoDB', 'Redis'],
          duration: '5-7 months',
          difficulty: 'Advanced',
          category: 'Marketing Tech',
          features: ['Campaign Management', 'Customer Segmentation', 'ROI Analytics', 'Email Automation', 'Social Media Integration']
        }
      ]
    },
    startup: {
      title: 'Startup Ideas',
      subtitle: 'Innovative Project Ideas to Launch Your Own Startup',
      icon: '🚀',
      projects: [
        {
          title: 'EcoTrack - Sustainability App',
          description: 'Mobile app that helps users track their carbon footprint, suggests eco-friendly alternatives, and connects them with local green initiatives.',
          technologies: ['React Native', 'Node.js', 'MongoDB', 'Google Maps API'],
          duration: '4-5 months',
          difficulty: 'Intermediate',
          category: 'Environmental Tech',
          features: ['Carbon Tracking', 'Eco Recommendations', 'Community Features', 'Gamification', 'Local Business Integration']
        },
        {
          title: 'SkillShare Platform',
          description: 'Peer-to-peer skill sharing platform where people can teach and learn from each other, with video calls, payment processing, and rating system.',
          technologies: ['React', 'WebRTC', 'Stripe', 'Firebase'],
          duration: '6-8 months',
          difficulty: 'Advanced',
          category: 'EdTech',
          features: ['Video Conferencing', 'Payment Processing', 'Skill Matching', 'Rating System', 'Schedule Management']
        },
        {
          title: 'LocalBiz - Hyperlocal Marketplace',
          description: 'Hyperlocal marketplace connecting neighborhood businesses with residents, featuring delivery tracking and community reviews.',
          technologies: ['Flutter', 'Firebase', 'Google Maps', 'Payment APIs'],
          duration: '5-6 months',
          difficulty: 'Advanced',
          category: 'E-commerce',
          features: ['Location-based Services', 'Delivery Tracking', 'Community Reviews', 'Multi-vendor Support', 'Real-time Chat']
        },
        {
          title: 'MoodMentor - Mental Health AI',
          description: 'AI-powered mental health companion that provides personalized suggestions, mood tracking, and connects users with professional help when needed.',
          technologies: ['Python', 'TensorFlow', 'React', 'NLP'],
          duration: '6-7 months',
          difficulty: 'Expert',
          category: 'HealthTech',
          features: ['AI Chatbot', 'Mood Analytics', 'Professional Network', 'Privacy-focused', 'Personalized Insights']
        }
      ]
    },
    realtime: {
      title: 'Real-time Experience',
      subtitle: 'Live Projects for Hands-on Industry Experience',
      icon: '⚡',
      projects: [
        {
          title: 'Live E-commerce Platform Development',
          description: 'Work on a real e-commerce platform used by actual customers. Contribute to features, bug fixes, and performance optimizations under mentor guidance.',
          technologies: ['React', 'Node.js', 'AWS', 'MongoDB'],
          duration: '3-6 months',
          difficulty: 'Intermediate',
          category: 'Production System',
          features: ['Live User Base', 'Code Reviews', 'Deployment Pipeline', 'Performance Monitoring', 'Team Collaboration']
        },
        {
          title: 'Open Source Contribution Program',
          description: 'Contribute to popular open source projects, work with global development teams, and build your GitHub profile with meaningful contributions.',
          technologies: ['Various', 'Git', 'GitHub', 'Documentation'],
          duration: '2-4 months',
          difficulty: 'All Levels',
          category: 'Open Source',
          features: ['Global Collaboration', 'Code Reviews', 'Documentation', 'Community Building', 'Portfolio Development']
        },
        {
          title: 'Startup MVP Development',
          description: 'Work directly with startup founders to build their Minimum Viable Product (MVP) from concept to launch, gaining real entrepreneurial experience.',
          technologies: ['Modern Stack', 'Cloud Platforms', 'Agile'],
          duration: '3-5 months',
          difficulty: 'Advanced',
          category: 'Startup Environment',
          features: ['MVP Development', 'User Testing', 'Market Validation', 'Agile Methodology', 'Founder Mentorship']
        },
        {
          title: 'Client Project Internship',
          description: 'Work on real client projects under senior developer supervision. Experience the full software development lifecycle from requirements to deployment.',
          technologies: ['Client-specific', 'Professional Tools', 'Industry Standards'],
          duration: '4-6 months',
          difficulty: 'Intermediate',
          category: 'Client Work',
          features: ['Client Interaction', 'Professional Environment', 'Quality Assurance', 'Project Management', 'Industry Exposure']
        }
      ]
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-orange-100 text-orange-800';
      case 'Expert': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleEnrollClick = () => {
    alert('Project enrollment will be available soon. Contact us for more details!');
  };

  return (
    <section id="projects" className="projects">
      <style>{`
        .projects {
          padding: 6rem 0;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 1rem;
        }

        .section-subtitle {
          font-size: 1.2rem;
          color: #64748b;
          max-width: 600px;
          margin: 0 auto;
        }

        .tab-navigation {
          display: flex;
          justify-content: center;
          margin-bottom: 3rem;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .tab-button {
          background: ${activeTab === 'academic' ? 'linear-gradient(135deg, #3b82f6, #8b5cf6)' : 'white'};
          color: ${activeTab === 'academic' ? 'white' : '#64748b'};
          border: 2px solid #e2e8f0;
          padding: 1rem 2rem;
          border-radius: 50px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1rem;
        }

        .tab-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
        }

        .tab-button.active {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          color: white;
          border-color: transparent;
        }

        .category-header {
          text-align: center;
          margin-bottom: 3rem;
          padding: 2rem;
          background: white;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .category-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .category-title {
          font-size: 2rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .category-subtitle {
          color: #64748b;
          font-size: 1.1rem;
        }

        .projects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 2rem;
        }

        .project-card {
          background: white;
          border-radius: 20px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .project-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
        }

        .project-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .project-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
        }

        .project-title {
          font-size: 1.4rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .project-category {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .project-description {
          color: #64748b;
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        .project-meta {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          color: #475569;
        }

        .difficulty-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .tech-stack {
          margin-bottom: 1.5rem;
        }

        .tech-label {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
        }

        .tech-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .tech-tag {
          background: #f1f5f9;
          color: #475569;
          padding: 0.25rem 0.75rem;
          border-radius: 15px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .project-features {
          margin-bottom: 1.5rem;
        }

        .features-label {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
        }

        .features-list {
          list-style: none;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 0.5rem;
        }

        .feature-item {
          color: #475569;
          font-size: 0.85rem;
          position: relative;
          padding-left: 1rem;
        }

        .feature-item::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: #10b981;
          font-weight: bold;
        }

        .project-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .duration-info {
          color: #64748b;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .explore-button {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          color: white;
          padding: 0.7rem 1.5rem;
          border: none;
          border-radius: 50px;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.3s ease;
          font-size: 0.9rem;
        }

        .explore-button:hover {
          transform: scale(1.05);
        }

        @media (max-width: 768px) {
          .projects-grid {
            grid-template-columns: 1fr;
          }
          
          .tab-navigation {
            flex-direction: column;
            align-items: center;
          }
          
          .tab-button {
            width: 100%;
            max-width: 300px;
            justify-content: center;
          }
          
          .project-meta {
            flex-direction: column;
            gap: 0.5rem;
          }
        }
      `}</style>

      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Project Portfolio</h2>
          <p className="section-subtitle">
            Gain hands-on experience with real-world projects across different categories. 
            From academic projects to startup ideas, build your portfolio with industry-relevant experience.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          {Object.entries(projectCategories).map(([key, category]) => (
            <button
              key={key}
              className={`tab-button ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <span>{category.icon}</span>
              {category.title}
            </button>
          ))}
        </div>

        {/* Category Header */}
        <div className="category-header">
          <div className="category-icon">{projectCategories[activeTab].icon}</div>
          <h3 className="category-title">{projectCategories[activeTab].title}</h3>
          <p className="category-subtitle">{projectCategories[activeTab].subtitle}</p>
        </div>

        {/* Projects Grid */}
        <div className="projects-grid">
          {projectCategories[activeTab].projects.map((project, index) => (
            <div key={index} className="project-card">
              <div className="project-header">
                <div>
                  <h4 className="project-title">{project.title}</h4>
                </div>
                <div className="project-category">{project.category}</div>
              </div>

              <p className="project-description">{project.description}</p>

              <div className="project-meta">
                <div className="meta-item">
                  <span>⏱️</span>
                  <span>{project.duration}</span>
                </div>
                <div className="meta-item">
                  <span className={`difficulty-badge ${getDifficultyColor(project.difficulty)}`}>
                    {project.difficulty}
                  </span>
                </div>
              </div>

              <div className="tech-stack">
                <div className="tech-label">Technologies:</div>
                <div className="tech-tags">
                  {project.technologies.map((tech, idx) => (
                    <span key={idx} className="tech-tag">{tech}</span>
                  ))}
                </div>
              </div>

              <div className="project-features">
                <div className="features-label">Key Features:</div>
                <ul className="features-list">
                  {project.features.map((feature, idx) => (
                    <li key={idx} className="feature-item">{feature}</li>
                  ))}
                </ul>
              </div>

              <div className="project-footer">
                <div className="duration-info">
                  <span>📅</span>
                  <span>Duration: {project.duration}</span>
                </div>
                <button className="explore-button" onClick={handleEnrollClick}>
                  Explore Project
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;